import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { GraphUpIcon } from '@synoptic/ui-kit/icons/react/graph-up.js';
import { NavArrowRightIcon } from '@synoptic/ui-kit/icons/react/nav-arrow-right.js';
import { composeEventHandlers } from '@synoptic/ui-kit/utils/compose-event-handlers.js';
import clsx from 'clsx';
import { HTMLAttributes, useState } from 'react';
import { useMatch } from 'react-router';
import { useAuthGuard } from '@/components/auth-guard/auth-guard-provider';
import { flags } from '@/feature-flags/flags';
import { useFeatureFlags } from '@/feature-flags/use-feature-flags';
import { paths } from '@/routes-utils/paths.ts';
import {
  setActiveTerminalTabId,
  useActiveTerminalTabId,
} from '@/routes/_app.p.terminal._index/active-terminal-tab-id';
import { useTerminalTabs } from '@/routes/_app.p.terminal._index/use-terminal-tabs';
import { useAppLoader } from '@/routes/_app/loader.ts';
import {
  CollapsibleMenuAction,
  CollapsibleMenuTrigger,
} from '@/routes/_app/sidebar/menu/menu-action/collapsible-menu-action.tsx';
import { MenuActionLink } from '@/routes/_app/sidebar/menu/menu-action/menu-action-link.tsx';
import { MenuSubActionLink } from '@/routes/_app/sidebar/menu/menu-action/menu-sub-action-link.tsx';
import { RequestAccessMenuModal } from './request-access-menu-modal';
import { useRequestTerminalAccess } from './use-request-terminal-access';
import { navArrowButton, navArrowDown, navArrowUp } from './menu-action.css';
import { streamDeckSubMenuLink } from './menu-action.css';

const TABS_LIMIT_ON_INIT = 4;

export const TerminalSubMenu = (props: HTMLAttributes<HTMLElement>) => {
  const { authenticated } = useAppLoader();

  if (!authenticated) {
    return <TerminalMenuActionLink {...props} />;
  }

  return <AuthenticatedTerminalSubMenu {...props} />;
};

const TerminalMenuActionLink = ({
  onClick,
  ...props
}: HTMLAttributes<HTMLElement>) => {
  const guard = useAuthGuard();

  return (
    <MenuActionLink
      icon={<GraphUpIcon />}
      to={paths.terminal}
      label={'Terminal'}
      {...props}
      onClick={composeEventHandlers(onClick, (e) => {
        if (guard()) {
          e.preventDefault();
        }
      })}
    />
  );
};

const AuthenticatedTerminalSubMenu = (props: HTMLAttributes<HTMLElement>) => {
  const isActiveTerminalRoute = Boolean(useMatch(paths.terminal));

  const terminalEnabled = useFeatureFlags(flags.terminal);

  const terminalTabs = useTerminalTabs();

  const activeTabId = useActiveTerminalTabId();

  const [isMoreTabsOpen, setIsMoreTabsOpen] = useState(false);

  const visibleTabs = isMoreTabsOpen
    ? terminalTabs
    : terminalTabs?.slice(0, TABS_LIMIT_ON_INIT);

  if (!terminalEnabled) {
    return <TerminalRequestAccessModal {...props} />;
  }

  if (!isActiveTerminalRoute || terminalTabs?.length < 2) {
    return <TerminalMenuActionLink {...props} />;
  }

  return (
    <CollapsibleMenuAction
      defaultOpen={isActiveTerminalRoute}
      collapsedActionLink={<TerminalMenuActionLink {...props} />}
      trigger={
        <CollapsibleMenuTrigger
          active={isActiveTerminalRoute}
          icon={<GraphUpIcon />}
          label={'Terminal'}
        />
      }
    >
      {visibleTabs.map((tab) => (
        <MenuSubActionLink
          key={tab.id}
          to={paths.terminal}
          onClick={() => setActiveTerminalTabId(tab.id)}
          label={tab.name}
          className={clsx({
            [streamDeckSubMenuLink]: activeTabId !== tab.id,
          })}
        />
      ))}
      {terminalTabs?.length > TABS_LIMIT_ON_INIT && (
        <Button
          className={navArrowButton}
          onClick={() => setIsMoreTabsOpen(!isMoreTabsOpen)}
        >
          {isMoreTabsOpen ? 'less tabs' : 'more tabs'}
          <NavArrowRightIcon
            className={isMoreTabsOpen ? navArrowUp : navArrowDown}
          />
        </Button>
      )}
    </CollapsibleMenuAction>
  );
};

const TerminalRequestAccessModal = ({
  className,
}: HTMLAttributes<HTMLElement>) => {
  const { requestAccess, loading, alreadyRequested } =
    useRequestTerminalAccess();

  return (
    <RequestAccessMenuModal
      label="Terminal"
      icon={<GraphUpIcon />}
      modalDescriptionContent="Join the waitlist to get early access to our trading terminal."
      modalActionsContent={
        <Button
          variant="primary"
          onClick={() => requestAccess()}
          disabled={alreadyRequested || loading}
          loading={loading}
          fullWidth
        >
          {alreadyRequested ? 'Access Requested' : 'Request Access'}
        </Button>
      }
      className={className}
    />
  );
};
